/**
 * F17 - Grafici Interattivi
 * Componenti chart per statistiche club
 */

import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, Cell,
  PieChart, Pie
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Colori per i grafici
const COLORS = {
  primary: '#3B82F6',
  secondary: '#8B5CF6', 
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  gray: '#6B7280',
  players: ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6']
};

/**
 * 📈 GRAFICO 1: TREND PUNTI RANKING
 * Mostra l'andamento dei punti di un giocatore torneo dopo torneo
 * Supporta sia palmares (tornei completati) che dettaglio_tornei (tutti)
 */
export const TrendPuntiChart = ({ data, dettaglioTornei, nomeGiocatore }) => {
  // Usa palmares se disponibile, altrimenti dettaglio_tornei
  const sourceData = (data && data.length > 0) ? data : dettaglioTornei;
  
  if (!sourceData || sourceData.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <div className="text-4xl mb-2">📈</div>
        <p>Nessun dato disponibile per il trend</p>
        <p className="text-sm mt-2">Partecipa ai tornei per vedere il tuo andamento!</p>
      </div>
    );
  }

  // Determina se stiamo usando palmares o dettaglio
  const isPalmares = data && data.length > 0;

  // Prepara dati per il grafico - accumula i punti
  const chartData = sourceData.map((item, index) => {
    const puntiCumulativi = sourceData.slice(0, index + 1).reduce((sum, t) => sum + (t.punti_ranking || 0), 0);
    return {
      name: item.torneo?.substring(0, 12) + (item.torneo?.length > 12 ? '...' : '') || `T${index + 1}`,
      fullName: item.torneo || `Torneo ${index + 1}`,
      punti: item.punti_ranking || 0,
      puntiCumulativi,
      posizione: item.posizione || '-',
      stagione: item.stagione || '',
      partite: item.partite || 0,
      gol: item.gol_fatti || 0,
      vinte: item.vinte || 0
    };
  });

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border">
          <p className="font-bold text-gray-800">{d.fullName}</p>
          <p className="text-sm text-gray-500">{d.stagione}</p>
          {isPalmares ? (
            <>
              <p className="text-blue-600">Posizione: <strong>{d.posizione}°</strong></p>
              <p className="text-green-600">Punti torneo: <strong>+{d.punti}</strong></p>
              <p className="text-purple-600">Punti totali: <strong>{d.puntiCumulativi}</strong></p>
            </>
          ) : (
            <>
              <p className="text-blue-600">Partite: <strong>{d.partite}</strong> ({d.vinte}V)</p>
              <p className="text-green-600">Gol: <strong>{d.gol}</strong></p>
            </>
          )}
        </div>
      );
    }
    return null;
  };

  // Se è dettaglio tornei (non palmares), mostra gol invece di punti
  const dataKey1 = isPalmares ? 'puntiCumulativi' : 'gol';
  const dataKey2 = isPalmares ? 'punti' : 'vinte';
  const label1 = isPalmares ? 'Punti Totali' : 'Gol Segnati';
  const label2 = isPalmares ? 'Punti Torneo' : 'Vittorie';

  return (
    <div className="w-full">
      <div className="mb-4">
        <h4 className="font-bold text-gray-700">📈 {isPalmares ? 'Trend Punti Ranking' : 'Statistiche per Torneo'}</h4>
        <p className="text-sm text-gray-500">
          {isPalmares 
            ? `Andamento punti cumulativi per ${nomeGiocatore}`
            : `Rendimento nei tornei per ${nomeGiocatore}`
          }
        </p>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis 
            dataKey="name" 
            tick={{ fontSize: 11 }} 
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Line 
            type="monotone" 
            dataKey={dataKey1}
            name={label1}
            stroke={COLORS.primary} 
            strokeWidth={3}
            dot={{ r: 6, fill: COLORS.primary }}
            activeDot={{ r: 8 }}
          />
          <Line 
            type="monotone" 
            dataKey={dataKey2}
            name={label2}
            stroke={COLORS.success} 
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ r: 4, fill: COLORS.success }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * 🎯 GRAFICO 2: CONFRONTO GIOCATORI (RADAR)
 * Confronta le statistiche di 2-3 giocatori
 */
export const ConfrontoGiocatoriChart = ({ giocatori }) => {
  if (!giocatori || giocatori.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <div className="text-4xl mb-2">🎯</div>
        <p>Seleziona almeno un giocatore per il confronto</p>
      </div>
    );
  }

  // Trova i valori massimi per normalizzare (0-100)
  const maxVittorie = Math.max(...giocatori.map(g => g.perc_vittorie || 0), 1);
  const maxGol = Math.max(...giocatori.map(g => g.gol_fatti || 0), 1);
  const maxPartite = Math.max(...giocatori.map(g => g.partite || 0), 1);
  const maxPodi = Math.max(...giocatori.map(g => (g.tornei_vinti || 0) + (g.secondi_posti || 0) + (g.terzi_posti || 0)), 1);
  const maxPunti = Math.max(...giocatori.map(g => g.punti_ranking_totali || 0), 1);

  // Prepara dati per radar
  const radarData = [
    { 
      metric: 'Vittorie %', 
      fullMetric: 'Percentuale Vittorie',
      ...Object.fromEntries(giocatori.map(g => [g.nome, g.perc_vittorie || 0]))
    },
    { 
      metric: 'Gol', 
      fullMetric: 'Gol Segnati',
      ...Object.fromEntries(giocatori.map(g => [g.nome, Math.round((g.gol_fatti || 0) / maxGol * 100)]))
    },
    { 
      metric: 'Partite', 
      fullMetric: 'Partite Giocate',
      ...Object.fromEntries(giocatori.map(g => [g.nome, Math.round((g.partite || 0) / maxPartite * 100)]))
    },
    { 
      metric: 'Podi', 
      fullMetric: 'Podi Totali',
      ...Object.fromEntries(giocatori.map(g => {
        const podi = (g.tornei_vinti || 0) + (g.secondi_posti || 0) + (g.terzi_posti || 0);
        return [g.nome, Math.round(podi / maxPodi * 100)];
      }))
    },
    { 
      metric: 'Ranking', 
      fullMetric: 'Punti Ranking',
      ...Object.fromEntries(giocatori.map(g => [g.nome, Math.round((g.punti_ranking_totali || 0) / maxPunti * 100)]))
    }
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border">
          <p className="font-bold text-gray-800 mb-2">{payload[0]?.payload?.fullMetric}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: <strong>{entry.value}</strong>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full">
      <div className="mb-4">
        <h4 className="font-bold text-gray-700">🎯 Confronto Giocatori</h4>
        <p className="text-sm text-gray-500">Valori normalizzati su scala 0-100</p>
      </div>
      
      {/* Legenda giocatori */}
      <div className="flex flex-wrap gap-3 mb-4 justify-center">
        {giocatori.map((g, i) => (
          <div key={g.nome} className="flex items-center gap-2">
            <div 
              className="w-4 h-4 rounded-full" 
              style={{ backgroundColor: COLORS.players[i % COLORS.players.length] }}
            />
            <span className="text-sm font-medium">{g.nome}</span>
          </div>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={350}>
        <RadarChart data={radarData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
          <PolarGrid stroke="#E5E7EB" />
          <PolarAngleAxis 
            dataKey="metric" 
            tick={{ fontSize: 12, fill: '#374151' }}
          />
          <PolarRadiusAxis 
            angle={90} 
            domain={[0, 100]} 
            tick={{ fontSize: 10 }}
          />
          {giocatori.map((g, i) => (
            <Radar
              key={g.nome}
              name={g.nome}
              dataKey={g.nome}
              stroke={COLORS.players[i % COLORS.players.length]}
              fill={COLORS.players[i % COLORS.players.length]}
              fillOpacity={0.2}
              strokeWidth={2}
            />
          ))}
          <Tooltip content={<CustomTooltip />} />
          <Legend />
        </RadarChart>
      </ResponsiveContainer>

      {/* Tabella dettagli */}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-3 py-2 text-left">Giocatore</th>
              <th className="px-3 py-2 text-center">Partite</th>
              <th className="px-3 py-2 text-center">V%</th>
              <th className="px-3 py-2 text-center">Gol</th>
              <th className="px-3 py-2 text-center">🏆</th>
              <th className="px-3 py-2 text-center">Punti</th>
            </tr>
          </thead>
          <tbody>
            {giocatori.map((g, i) => (
              <tr key={g.nome} className="border-b">
                <td className="px-3 py-2 font-medium flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: COLORS.players[i % COLORS.players.length] }}
                  />
                  {g.nome}
                </td>
                <td className="px-3 py-2 text-center">{g.partite || 0}</td>
                <td className="px-3 py-2 text-center text-green-600 font-bold">{g.perc_vittorie || 0}%</td>
                <td className="px-3 py-2 text-center text-purple-600">{g.gol_fatti || 0}</td>
                <td className="px-3 py-2 text-center text-yellow-600">{g.tornei_vinti || 0}</td>
                <td className="px-3 py-2 text-center text-blue-600 font-bold">{g.punti_ranking_totali || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * 🔍 SELETTORE GIOCATORI PER CONFRONTO
 * Permette di selezionare fino a 3 giocatori da confrontare
 */
export const SelettoreGiocatoriConfronto = ({ 
  giocatoriDisponibili, 
  giocatoriSelezionati, 
  onSelectionChange,
  maxSelection = 3 
}) => {
  const toggleGiocatore = (giocatore) => {
    const isSelected = giocatoriSelezionati.some(g => g.nome === giocatore.nome);
    
    if (isSelected) {
      onSelectionChange(giocatoriSelezionati.filter(g => g.nome !== giocatore.nome));
    } else if (giocatoriSelezionati.length < maxSelection) {
      onSelectionChange([...giocatoriSelezionati, giocatore]);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-700">Seleziona giocatori (max {maxSelection})</h4>
        <span className="text-sm text-gray-500">
          {giocatoriSelezionati.length}/{maxSelection} selezionati
        </span>
      </div>
      
      <div className="flex flex-wrap gap-2">
        {giocatoriDisponibili.map((g, i) => {
          const isSelected = giocatoriSelezionati.some(sel => sel.nome === g.nome);
          const canSelect = isSelected || giocatoriSelezionati.length < maxSelection;
          
          return (
            <button
              key={g.nome || i}
              onClick={() => toggleGiocatore(g)}
              disabled={!canSelect}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                isSelected 
                  ? 'bg-blue-600 text-white shadow-md' 
                  : canSelect
                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    : 'bg-gray-50 text-gray-400 cursor-not-allowed'
              }`}
            >
              {g.nome}
              {isSelected && <span className="ml-1">✓</span>}
            </button>
          );
        })}
      </div>
      
      {giocatoriSelezionati.length > 0 && (
        <button
          onClick={() => onSelectionChange([])}
          className="text-sm text-red-600 hover:text-red-700"
        >
          ✕ Deseleziona tutti
        </button>
      )}
    </div>
  );
};

/**
 * 📊 WRAPPER COMPLETO PER SEZIONE CONFRONTO
 * Include selettore + grafico radar
 */
export const SezioneConfrontoGiocatori = ({ token, clubStats }) => {
  const [giocatoriSelezionati, setGiocatoriSelezionati] = useState([]);
  
  // Usa i dati già disponibili dalle statistiche club
  const giocatoriDisponibili = clubStats?.classifica_stagionale || clubStats?.soci_stats || [];

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 space-y-6">
      <div className="border-b pb-4">
        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <span>🎯</span> Confronto Giocatori
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Seleziona fino a 3 giocatori per confrontare le loro statistiche
        </p>
      </div>

      <SelettoreGiocatoriConfronto
        giocatoriDisponibili={giocatoriDisponibili}
        giocatoriSelezionati={giocatoriSelezionati}
        onSelectionChange={setGiocatoriSelezionati}
      />

      {giocatoriSelezionati.length > 0 && (
        <div className="pt-4 border-t">
          <ConfrontoGiocatoriChart giocatori={giocatoriSelezionati} />
        </div>
      )}
    </div>
  );
};

/**
 * ⚽ GRAFICO 3: TOP MARCATORI (BAR CHART ORIZZONTALE)
 * Mostra la classifica dei migliori marcatori
 */
export const TopMarcatoriChart = ({ data, maxItems = 10 }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <div className="text-4xl mb-2">⚽</div>
        <p>Nessun dato disponibile</p>
      </div>
    );
  }

  // Prendi i top marcatori e ordina per gol
  const chartData = [...data]
    .sort((a, b) => (b.gol_fatti || b.gol || 0) - (a.gol_fatti || a.gol || 0))
    .slice(0, maxItems)
    .map((player, index) => ({
      nome: player.nome,
      gol: player.gol_fatti || player.gol || 0,
      partite: player.partite || 0,
      mediaGol: player.partite > 0 ? ((player.gol_fatti || player.gol || 0) / player.partite).toFixed(2) : 0,
      rank: index + 1
    }));

  // Colori gradient dal più alto al più basso
  const getBarColor = (index) => {
    const colors = ['#9333EA', '#A855F7', '#C084FC', '#D8B4FE', '#E9D5FF', '#F3E8FF', '#FAF5FF', '#FDFCFF'];
    return colors[Math.min(index, colors.length - 1)];
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border">
          <p className="font-bold text-gray-800">{d.nome}</p>
          <p className="text-purple-600">Gol: <strong>{d.gol}</strong></p>
          <p className="text-gray-500">Partite: {d.partite}</p>
          <p className="text-green-600">Media: {d.mediaGol} gol/partita</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full">
      <div className="mb-4">
        <h4 className="font-bold text-gray-700 flex items-center gap-2">
          <span>⚽</span> Top Marcatori
        </h4>
        <p className="text-sm text-gray-500">Classifica capocannonieri stagione</p>
      </div>
      <ResponsiveContainer width="100%" height={Math.max(250, chartData.length * 40)}>
        <BarChart 
          data={chartData} 
          layout="vertical"
          margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={true} vertical={false} />
          <XAxis type="number" tick={{ fontSize: 12 }} />
          <YAxis 
            type="category" 
            dataKey="nome" 
            tick={{ fontSize: 12 }} 
            width={75}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="gol" name="Gol" radius={[0, 4, 4, 0]}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getBarColor(index)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      
      {/* Podio visivo */}
      {chartData.length >= 3 && (
        <div className="flex justify-center gap-4 mt-4">
          {[1, 0, 2].map((podiumIndex) => {
            const player = chartData[podiumIndex];
            if (!player) return null;
            const heights = { 0: 'h-24', 1: 'h-20', 2: 'h-16' };
            const medals = { 0: '🥇', 1: '🥈', 2: '🥉' };
            const bgColors = { 0: 'bg-yellow-400', 1: 'bg-gray-300', 2: 'bg-orange-400' };
            return (
              <div key={podiumIndex} className="text-center">
                <div className="text-2xl mb-1">{medals[podiumIndex]}</div>
                <div className="text-sm font-medium truncate max-w-[80px]">{player.nome}</div>
                <div className="text-purple-600 font-bold">{player.gol} ⚽</div>
                <div className={`${heights[podiumIndex]} w-16 ${bgColors[podiumIndex]} rounded-t-lg mt-2 flex items-end justify-center pb-1`}>
                  <span className="text-white font-bold">{podiumIndex + 1}°</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * 🥧 GRAFICO 4: DISTRIBUZIONE RISULTATI (DONUT CHART)
 * Mostra la percentuale di vittorie/pareggi/sconfitte per un giocatore
 */
export const DistribuzioneRisultatiChart = ({ vinte, pareggiate, perse, nomeGiocatore }) => {
  const totale = (vinte || 0) + (pareggiate || 0) + (perse || 0);
  
  if (totale === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <div className="text-4xl mb-2">📊</div>
        <p>Nessuna partita giocata</p>
      </div>
    );
  }

  const chartData = [
    { name: 'Vittorie', value: vinte || 0, color: '#10B981', emoji: '✅' },
    { name: 'Pareggi', value: pareggiate || 0, color: '#6B7280', emoji: '➖' },
    { name: 'Sconfitte', value: perse || 0, color: '#EF4444', emoji: '❌' }
  ].filter(d => d.value > 0);

  const percVittorie = ((vinte || 0) / totale * 100).toFixed(1);
  const percPareggi = ((pareggiate || 0) / totale * 100).toFixed(1);
  const percSconfitte = ((perse || 0) / totale * 100).toFixed(1);

  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    if (percent < 0.05) return null; // Non mostrare label se < 5%
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor="middle" 
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="w-full">
      <div className="mb-4">
        <h4 className="font-bold text-gray-700 flex items-center gap-2">
          <span>📊</span> Distribuzione Risultati
        </h4>
        <p className="text-sm text-gray-500">{totale} partite totali</p>
      </div>
      
      <div className="flex flex-col md:flex-row items-center gap-6">
        {/* Donut Chart */}
        <div className="w-full md:w-1/2">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                labelLine={false}
                label={CustomLabel}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name) => [`${value} partite (${((value/totale)*100).toFixed(1)}%)`, name]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        
        {/* Legenda e stats */}
        <div className="w-full md:w-1/2 space-y-3">
          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">
              {vinte || 0}
            </div>
            <div className="flex-1">
              <p className="font-medium text-green-800">Vittorie</p>
              <p className="text-sm text-green-600">{percVittorie}%</p>
            </div>
            <div className="text-2xl">✅</div>
          </div>
          
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-gray-500 rounded-full flex items-center justify-center text-white font-bold">
              {pareggiate || 0}
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-800">Pareggi</p>
              <p className="text-sm text-gray-600">{percPareggi}%</p>
            </div>
            <div className="text-2xl">➖</div>
          </div>
          
          <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
            <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center text-white font-bold">
              {perse || 0}
            </div>
            <div className="flex-1">
              <p className="font-medium text-red-800">Sconfitte</p>
              <p className="text-sm text-red-600">{percSconfitte}%</p>
            </div>
            <div className="text-2xl">❌</div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 📅 GRAFICO 5: STORICO STAGIONI (BAR CHART VERTICALE)
 * Mostra il confronto tra le stagioni archiviate
 */
export const StoricoStagioniChart = ({ stagioni }) => {
  if (!stagioni || stagioni.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <div className="text-4xl mb-2">📅</div>
        <p>Nessuna stagione archiviata</p>
        <p className="text-sm mt-2">Archivia le stagioni per vedere lo storico!</p>
      </div>
    );
  }

  // Prepara i dati per il grafico
  const chartData = stagioni.map(s => ({
    stagione: s.stagione,
    campione: s.campione?.nome || 'N/D',
    puntiCampione: s.campione?.punti_ranking_totali || 0,
    golCampione: s.campione?.gol_fatti || 0,
    partiteCampione: s.campione?.partite || 0,
    torneiGiocati: s.statistiche?.tornei_totali || s.campione?.tornei_giocati || 0,
    partecipanti: s.statistiche?.partecipanti_unici || 0,
    golTotali: s.statistiche?.gol_totali || 0,
    partiteTotali: s.statistiche?.partite_totali || 0
  })).reverse(); // Ordine cronologico

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const d = chartData.find(item => item.stagione === label);
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border">
          <p className="font-bold text-gray-800 text-lg mb-2">Stagione {label}</p>
          <div className="space-y-1 text-sm">
            <p className="text-yellow-600">🏆 Campione: <strong>{d?.campione}</strong></p>
            <p className="text-blue-600">📊 Punti: <strong>{d?.puntiCampione}</strong></p>
            <p className="text-purple-600">⚽ Gol campione: <strong>{d?.golCampione}</strong></p>
            <p className="text-green-600">🎮 Tornei: <strong>{d?.torneiGiocati}</strong></p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full">
      <div className="mb-4">
        <h4 className="font-bold text-gray-700 flex items-center gap-2">
          <span>📅</span> Storico Stagioni
        </h4>
        <p className="text-sm text-gray-500">Confronto tra le stagioni archiviate</p>
      </div>

      {/* Grafico principale - Punti del campione per stagione */}
      <div className="mb-6">
        <p className="text-xs text-gray-500 mb-2 font-medium">Punti Ranking del Campione</p>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
            <XAxis 
              dataKey="stagione" 
              tick={{ fontSize: 11 }} 
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="puntiCampione" name="Punti Campione" radius={[4, 4, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={index === chartData.length - 1 ? '#F59E0B' : '#3B82F6'} 
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Grafico secondario - Gol del campione */}
      <div className="mb-6">
        <p className="text-xs text-gray-500 mb-2 font-medium">Gol del Campione</p>
        <ResponsiveContainer width="100%" height={150}>
          <BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
            <XAxis dataKey="stagione" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Bar dataKey="golCampione" name="Gol Campione" fill="#9333EA" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Tabella riepilogativa */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gradient-to-r from-amber-100 to-yellow-100">
              <th className="px-3 py-2 text-left">Stagione</th>
              <th className="px-3 py-2 text-left">🏆 Campione</th>
              <th className="px-3 py-2 text-center">Punti</th>
              <th className="px-3 py-2 text-center">Gol</th>
              <th className="px-3 py-2 text-center">Partite</th>
            </tr>
          </thead>
          <tbody>
            {[...chartData].reverse().map((s, i) => (
              <tr key={s.stagione} className={`border-b ${i === 0 ? 'bg-yellow-50' : ''}`}>
                <td className="px-3 py-2 font-medium">
                  {s.stagione}
                  {i === 0 && <span className="ml-2 text-xs bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded">Ultima</span>}
                </td>
                <td className="px-3 py-2">
                  <span className="flex items-center gap-1">
                    {i === 0 && <span>👑</span>}
                    {s.campione}
                  </span>
                </td>
                <td className="px-3 py-2 text-center font-bold text-blue-600">{s.puntiCampione}</td>
                <td className="px-3 py-2 text-center text-purple-600">{s.golCampione}</td>
                <td className="px-3 py-2 text-center text-gray-600">{s.partiteCampione}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Statistiche aggregate */}
      {stagioni.length > 1 && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-600">{stagioni.length}</div>
            <div className="text-xs text-gray-500">Stagioni</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {new Set(stagioni.map(s => s.campione?.nome)).size}
            </div>
            <div className="text-xs text-gray-500">Campioni diversi</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-purple-600">
              {Math.round(stagioni.reduce((sum, s) => sum + (s.campione?.punti_ranking_totali || 0), 0) / stagioni.length)}
            </div>
            <div className="text-xs text-gray-500">Media punti</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-600">
              {Math.max(...stagioni.map(s => s.campione?.punti_ranking_totali || 0))}
            </div>
            <div className="text-xs text-gray-500">Record punti</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default {
  TrendPuntiChart,
  ConfrontoGiocatoriChart,
  SelettoreGiocatoriConfronto,
  SezioneConfrontoGiocatori,
  TopMarcatoriChart,
  DistribuzioneRisultatiChart,
  StoricoStagioniChart
};
